// PurchaseTracker.js - multi-vendor Purchase Tracker (visual/UX matches SalesTracker)
import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  InputAdornment,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import { ThemeProvider, createTheme, alpha } from "@mui/material/styles";
import {
  AddRounded,
  EditRounded,
  DeleteRounded,
  VisibilityRounded,
  SearchRounded,
  FileDownloadRounded,
  InventoryRounded,
  PendingActionsRounded,
  LoopRounded,
  TaskAltRounded,
  LocalShippingRounded,
  PaymentsRounded,
  RefreshRounded,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { purchaseAPI, currencyAPI, vendorAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";

// Theme (exact match to SalesTracker)
const getTheme = () =>
  createTheme({
    palette: {
      mode: "light",
      primary: { main: "#005b82", light: "#2586bc", dark: "#003d5c" },
      background: { default: "#f8fafc", paper: "#ffffff" },
    },
    shape: { borderRadius: 12 },
  });

const STATUS_OPTS = [
  { value: "pending", label: "Pending" },
  { value: "ordered", label: "Ordered" },
  { value: "received", label: "Received" },
  { value: "closed", label: "Closed" },
  { value: "cancelled", label: "Cancelled" },
];

const emptyVendorDetail = {
  vendor: "",
  vendor_name: "",
  vendor_address: "",
  items: "",
  po_issued_to_vendor: "",
  item_received_from_vendor: "",
  deliver_status_from_vendor: "",
  deliver_date_from_vendor: "",
  purchase_remark: "",
  purchase_status: "pending",
  vendor_registration: "",
  pi_received_from_vendor: "",
  advance_payment_release_to_vendor: "",
  pending_payments_to_vendor: "",
  logistic_payment: "",
  final_tax_invoice_no_received_from_vendor: "",
  final_payment_release_to_vendor: "",
  final_payment_date_to_vendor: "",
  final_tax_invoice_from_vendor_status: "",
  accounts_remark: "",
  accounts_status: "pending",
  currency_advance: "",
  currency_logistic: "",
  currency_final_payment: "",
  currency_pending_payment: "",
};

const emptyPurchaseForm = {
  sr_no: "",
  vessel_name: "",
  received_po_from_client: "",
  assigned_team: "",
  status: "pending",
  vendor_details: [{ ...emptyVendorDetail }],
};

// Defined at module scope (not inline) so identity stays stable across
// re-renders - an inline component here would remount on every keystroke
// and drop focus.
const VendorInfoBlock = ({
  vd,
  index,
  vendors,
  disabled,
  onFieldChange,
  onVendorSelect,
  onRemove,
  canRemove,
}) => (
  <Card variant="outlined" sx={{ p: 2, mb: 2, background: alpha("#005b82", 0.02) }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={700} color="primary">
        Vendor {index + 1}
      </Typography>
      {canRemove && !disabled && (
        <Tooltip title="Remove this vendor">
          <IconButton size="small" color="error" onClick={() => onRemove(index)}>
            <DeleteRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    <Box sx={{ display: "grid", gap: 2 }}>
      <FormControl fullWidth>
        <InputLabel sx={{ fontWeight: "bold" }} disabled={disabled}>
          Select Vendor (Master)
        </InputLabel>
        <Select
          value={vd.vendor || ""}
          label="Select Vendor (Master)"
          onChange={(e) => onVendorSelect(index, e.target.value)}
          disabled={disabled}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {vendors.map((v) => (
            <MenuItem key={v.id} value={v.id}>
              {v.company_name || `Vendor #${v.sr_no}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          name="vendor_name"
          label="Vendor Name *"
          value={vd.vendor_name}
          onChange={(e) => onFieldChange(index, e)}
          fullWidth
          disabled={disabled}
          InputLabelProps={{ sx: { fontWeight: "bold" } }}
        />
        <TextField
          name="po_issued_to_vendor"
          label="Vendor PO No"
          value={vd.po_issued_to_vendor}
          onChange={(e) => onFieldChange(index, e)}
          fullWidth
          disabled={disabled}
          InputLabelProps={{ sx: { fontWeight: "bold" } }}
        />
      </Box>
      <TextField
        name="vendor_address"
        label="Vendor Address"
        value={vd.vendor_address}
        onChange={(e) => onFieldChange(index, e)}
        fullWidth
        multiline
        disabled={disabled}
        InputLabelProps={{ sx: { fontWeight: "bold" } }}
      />
      <TextField
        name="items"
        label="Items"
        value={vd.items}
        onChange={(e) => onFieldChange(index, e)}
        fullWidth
        multiline
        disabled={disabled}
        InputLabelProps={{ sx: { fontWeight: "bold" } }}
      />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          name="item_received_from_vendor"
          label="Item Received from Vendor"
          value={vd.item_received_from_vendor}
          onChange={(e) => onFieldChange(index, e)}
          fullWidth
          disabled={disabled}
          InputLabelProps={{ sx: { fontWeight: "bold" } }}
        />
        <TextField
          name="deliver_status_from_vendor"
          label="Deliver Status"
          value={vd.deliver_status_from_vendor}
          onChange={(e) => onFieldChange(index, e)}
          fullWidth
          disabled={disabled}
          InputLabelProps={{ sx: { fontWeight: "bold" } }}
        />
      </Box>
      <TextField
        name="deliver_date_from_vendor"
        label="Deliver Date"
        type="date"
        value={vd.deliver_date_from_vendor || ""}
        onChange={(e) => onFieldChange(index, e)}
        fullWidth
        disabled={disabled}
        InputLabelProps={{ shrink: true, sx: { fontWeight: "bold" } }}
      />
      <TextField
        name="purchase_remark"
        label="Purchase Remark"
        value={vd.purchase_remark}
        onChange={(e) => onFieldChange(index, e)}
        fullWidth
        multiline
        disabled={disabled}
        InputLabelProps={{ sx: { fontWeight: "bold" } }}
      />
      <FormControl fullWidth>
        <InputLabel sx={{ fontWeight: "bold" }} disabled={disabled}>
          Purchase Status
        </InputLabel>
        <Select
          name="purchase_status"
          value={vd.purchase_status}
          label="Purchase Status"
          onChange={(e) => onFieldChange(index, e)}
          disabled={disabled}
        >
          {STATUS_OPTS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  </Card>
);

const AccountsInfoBlock = ({ vd, index, disabled, currencies, onFieldChange }) => (
  <Card variant="outlined" sx={{ p: 2, mb: 2, background: alpha("#005b82", 0.02) }}>
    <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1 }}>
      Vendor {index + 1}{vd.vendor_name ? ` - ${vd.vendor_name}` : ""}
    </Typography>
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          name="vendor_registration"
          label="Vendor Registration"
          value={vd.vendor_registration}
          onChange={(e) => onFieldChange(index, e)}
          fullWidth
          disabled={disabled}
          InputLabelProps={{ sx: { fontWeight: "bold" } }}
        />
        <TextField
          name="pi_received_from_vendor"
          label="Vendor PI No"
          value={vd.pi_received_from_vendor}
          onChange={(e) => onFieldChange(index, e)}
          fullWidth
          disabled={disabled}
          InputLabelProps={{ sx: { fontWeight: "bold" } }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <FormControl fullWidth>
          <InputLabel sx={{ fontWeight: "bold" }} disabled={disabled}>
            Advance Currency
          </InputLabel>
          <Select
            name="currency_advance"
            value={vd.currency_advance}
            label="Advance Currency"
            onChange={(e) => onFieldChange(index, e)}
            disabled={disabled}
          >
            <MenuItem value="">
              <em>Select Currency</em>
            </MenuItem>
            {currencies.map((cur) => (
              <MenuItem key={cur.id} value={cur.id}>
                {cur.code} - {cur.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          name="advance_payment_release_to_vendor"
          label="Advance Payment"
          value={vd.advance_payment_release_to_vendor}
          onChange={(e) => onFieldChange(index, e)}
          fullWidth
          disabled={disabled}
          InputLabelProps={{ sx: { fontWeight: "bold" } }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <FormControl fullWidth>
          <InputLabel sx={{ fontWeight: "bold" }} disabled={disabled}>
            Pending Payment Currency
          </InputLabel>
          <Select
            name="currency_pending_payment"
            value={vd.currency_pending_payment}
            label="Pending Payment Currency"
            onChange={(e) => onFieldChange(index, e)}
            disabled={disabled}
          >
            <MenuItem value="">
              <em>Select Currency</em>
            </MenuItem>
            {currencies.map((cur) => (
              <MenuItem key={cur.id} value={cur.id}>
                {cur.code} - {cur.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          name="pending_payments_to_vendor"
          label="Pending Payments"
          value={vd.pending_payments_to_vendor}
          onChange={(e) => onFieldChange(index, e)}
          fullWidth
          disabled={disabled}
          InputLabelProps={{ sx: { fontWeight: "bold" } }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <FormControl fullWidth>
          <InputLabel sx={{ fontWeight: "bold" }} disabled={disabled}>
            Logistic Currency
          </InputLabel>
          <Select
            name="currency_logistic"
            value={vd.currency_logistic}
            label="Logistic Currency"
            onChange={(e) => onFieldChange(index, e)}
            disabled={disabled}
          >
            <MenuItem value="">
              <em>Select Currency</em>
            </MenuItem>
            {currencies.map((cur) => (
              <MenuItem key={cur.id} value={cur.id}>
                {cur.code} - {cur.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          name="logistic_payment"
          label="Logistic Payment"
          value={vd.logistic_payment}
          onChange={(e) => onFieldChange(index, e)}
          fullWidth
          disabled={disabled}
          InputLabelProps={{ sx: { fontWeight: "bold" } }}
        />
      </Box>

      <TextField
        name="final_tax_invoice_no_received_from_vendor"
        label="Final Tax Invoice No"
        value={vd.final_tax_invoice_no_received_from_vendor}
        onChange={(e) => onFieldChange(index, e)}
        fullWidth
        disabled={disabled}
        InputLabelProps={{ sx: { fontWeight: "bold" } }}
      />

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <FormControl fullWidth>
          <InputLabel sx={{ fontWeight: "bold" }} disabled={disabled}>
            Final Payment Currency
          </InputLabel>
          <Select
            name="currency_final_payment"
            value={vd.currency_final_payment}
            label="Final Payment Currency"
            onChange={(e) => onFieldChange(index, e)}
            disabled={disabled}
          >
            <MenuItem value="">
              <em>Select Currency</em>
            </MenuItem>
            {currencies.map((cur) => (
              <MenuItem key={cur.id} value={cur.id}>
                {cur.code} - {cur.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          name="final_payment_release_to_vendor"
          label="Final Payment"
          value={vd.final_payment_release_to_vendor}
          onChange={(e) => onFieldChange(index, e)}
          fullWidth
          disabled={disabled}
          InputLabelProps={{ sx: { fontWeight: "bold" } }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          name="final_payment_date_to_vendor"
          label="Final Payment Date"
          type="date"
          value={vd.final_payment_date_to_vendor || ""}
          onChange={(e) => onFieldChange(index, e)}
          fullWidth
          disabled={disabled}
          InputLabelProps={{ shrink: true, sx: { fontWeight: "bold" } }}
        />
        <TextField
          name="final_tax_invoice_from_vendor_status"
          label="Final Tax Invoice Status"
          value={vd.final_tax_invoice_from_vendor_status}
          onChange={(e) => onFieldChange(index, e)}
          fullWidth
          disabled={disabled}
          InputLabelProps={{ sx: { fontWeight: "bold" } }}
        />
      </Box>

      <TextField
        name="accounts_remark"
        label="Accounts Remark"
        value={vd.accounts_remark}
        onChange={(e) => onFieldChange(index, e)}
        fullWidth
        multiline
        disabled={disabled}
        InputLabelProps={{ sx: { fontWeight: "bold" } }}
      />

      <FormControl fullWidth>
        <InputLabel sx={{ fontWeight: "bold" }} disabled={disabled}>
          Accounts Status
        </InputLabel>
        <Select
          name="accounts_status"
          value={vd.accounts_status}
          label="Accounts Status"
          onChange={(e) => onFieldChange(index, e)}
          disabled={disabled}
        >
          {STATUS_OPTS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  </Card>
);

const PurchaseTracker = () => {
  const { user, canCreateModule, canUpdateModule, canDeleteModule } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [purchaseForm, setPurchaseForm] = useState(emptyPurchaseForm);

  // Permissions: create/update/delete follow the live Permissions matrix for
  // the purchase_tracker module. Which TEAM's fields are editable within a
  // record (Purchase-team vs Accounts-team) is a separate business rule tied
  // to role identity, not a generic module permission, so those stay
  // role-based - matching the same split the backend serializer enforces.
  const perms = {
    canCreate: () => canCreateModule("purchase_tracker"),
    canUpdate: () => canUpdateModule("purchase_tracker"),
    canEditPurchase: () => ["SALES", "PURCHASE", "SUPERADMIN"].includes(user?.role_name),
    canEditAccounts: () => ["ACCOUNTS", "SUPERADMIN"].includes(user?.role_name),
    canEditAll: () => user?.is_superuser || user?.role_name === "SUPERADMIN",
    canDelete: () => canDeleteModule("purchase_tracker"),
  };

  useEffect(() => {
    (async () => {
      await fetchPurchases();
      await fetchCurrencies();
      await fetchVendors();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const res = await purchaseAPI.getPurchases();
      setPurchases(res.data || []);
    } catch (e) {
      showAlert("Error fetching purchases", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const res = await currencyAPI.getCurrencies();
      setCurrencies(res.data || []);
    } catch (e) {
      showAlert("Warning: Failed to load currencies", "warning");
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await vendorAPI.getVendors();
      setVendors(res.data || []);
    } catch (e) {
      showAlert("Warning: Failed to load vendor master list", "warning");
    }
  };

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
    setTimeout(
      () => setAlert({ open: false, message: "", severity: "success" }),
      3200
    );
  };

  const statusLabel = (v) => STATUS_OPTS.find((o) => o.value === v)?.label || v;

  const vendorNames = (p) =>
    (p.vendor_details || []).map((vd) => vd.vendor_name).filter(Boolean);

  const statusSummary = (details, field) => {
    const vals = Array.from(
      new Set((details || []).map((vd) => vd[field]).filter(Boolean))
    );
    if (vals.length === 0) return "-";
    if (vals.length === 1) return statusLabel(vals[0]);
    return "Mixed";
  };

  const filtered = purchases.filter((p) => {
    const q = searchTerm.toLowerCase();
    const vendorNamesStr = vendorNames(p).join(" ").toLowerCase();
    const matchQ =
      (p.vessel_name || "").toLowerCase().includes(q) ||
      vendorNamesStr.includes(q) ||
      (p.received_po_from_client || "").toLowerCase().includes(q);
    const matchStatus = statusFilter === "all" || p.status === statusFilter;
    return matchQ && matchStatus;
  });

  const openModal = (purchase = null) => {
    if (!purchase && !perms.canCreate())
      return showAlert("You do not have permission", "error");

    if (purchase) {
      setEditingPurchase(purchase);
      const details =
        purchase.vendor_details && purchase.vendor_details.length > 0
          ? purchase.vendor_details.map((vd) => ({
              vendor: vd.vendor ?? "",
              vendor_name: vd.vendor_name ?? "",
              vendor_address: vd.vendor_address ?? "",
              items: vd.items ?? "",
              po_issued_to_vendor: vd.po_issued_to_vendor ?? "",
              item_received_from_vendor: vd.item_received_from_vendor ?? "",
              deliver_status_from_vendor: vd.deliver_status_from_vendor ?? "",
              deliver_date_from_vendor: vd.deliver_date_from_vendor ?? "",
              purchase_remark: vd.purchase_remark ?? "",
              purchase_status: vd.purchase_status ?? "pending",
              vendor_registration: vd.vendor_registration ?? "",
              pi_received_from_vendor: vd.pi_received_from_vendor ?? "",
              advance_payment_release_to_vendor:
                vd.advance_payment_release_to_vendor ?? "",
              pending_payments_to_vendor: vd.pending_payments_to_vendor ?? "",
              logistic_payment: vd.logistic_payment ?? "",
              final_tax_invoice_no_received_from_vendor:
                vd.final_tax_invoice_no_received_from_vendor ?? "",
              final_payment_release_to_vendor:
                vd.final_payment_release_to_vendor ?? "",
              final_payment_date_to_vendor:
                vd.final_payment_date_to_vendor ?? "",
              final_tax_invoice_from_vendor_status:
                vd.final_tax_invoice_from_vendor_status ?? "",
              accounts_remark: vd.accounts_remark ?? "",
              accounts_status: vd.accounts_status ?? "pending",
              currency_advance: vd.currency_advance ?? "",
              currency_logistic: vd.currency_logistic ?? "",
              currency_final_payment: vd.currency_final_payment ?? "",
              currency_pending_payment: vd.currency_pending_payment ?? "",
            }))
          : [{ ...emptyVendorDetail }];

      setPurchaseForm({
        sr_no: purchase.sr_no ?? "",
        vessel_name: purchase.vessel_name ?? "",
        received_po_from_client: purchase.received_po_from_client ?? "",
        assigned_team: purchase.assigned_team ?? "",
        status: purchase.status ?? "pending",
        vendor_details: details,
      });
    } else {
      setEditingPurchase(null);
      setPurchaseForm({
        ...emptyPurchaseForm,
        sr_no: calculateNextSrNo(),
        vendor_details: [{ ...emptyVendorDetail }],
      });
    }

    setActiveStep(0);
    setOpen(true);
  };

  const calculateNextSrNo = () => {
    if (purchases.length === 0) return "1";
    const srNumbers = purchases
      .map((p) => {
        if (!p.sr_no) return null;
        const sr = p.sr_no.toString().trim();
        if (!isNaN(sr) && sr !== "") return parseInt(sr, 10);
        const numericPart = sr.match(/\d+$/);
        if (numericPart) return parseInt(numericPart[0], 10);
        return null;
      })
      .filter((sr) => sr !== null && !isNaN(sr));
    if (srNumbers.length > 0) return (Math.max(...srNumbers) + 1).toString();
    return "1";
  };

  const closeModal = () => {
    setOpen(false);
    setEditingPurchase(null);
  };

  const onChange = (e) =>
    setPurchaseForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const updateVendorDetailFields = (index, fields) => {
    setPurchaseForm((prev) => ({
      ...prev,
      vendor_details: prev.vendor_details.map((vd, i) =>
        i === index ? { ...vd, ...fields } : vd
      ),
    }));
  };

  const onVendorFieldChange = (index, e) => {
    updateVendorDetailFields(index, { [e.target.name]: e.target.value });
  };

  const onVendorSelect = (index, vendorId) => {
    const vendorObj = vendors.find((v) => v.id === vendorId);
    const primaryAddr =
      vendorObj?.addresses?.find((a) => a.is_primary) ||
      vendorObj?.addresses?.[0];
    updateVendorDetailFields(index, {
      vendor: vendorId,
      vendor_name: vendorObj?.company_name || "",
      vendor_address: primaryAddr?.address || "",
    });
  };

  const addVendorDetail = () => {
    setPurchaseForm((prev) => ({
      ...prev,
      vendor_details: [...prev.vendor_details, { ...emptyVendorDetail }],
    }));
  };

  const removeVendorDetail = (index) => {
    setPurchaseForm((prev) => {
      if (prev.vendor_details.length <= 1) return prev;
      return {
        ...prev,
        vendor_details: prev.vendor_details.filter((_, i) => i !== index),
      };
    });
  };

  const onSubmit = async () => {
    if (!purchaseForm.sr_no || !purchaseForm.vessel_name)
      return showAlert("SR No & Vessel are required", "error");
    const hasVendorName = purchaseForm.vendor_details.some((vd) =>
      (vd.vendor_name || "").trim()
    );
    if (!hasVendorName)
      return showAlert("At least one vendor name is required", "error");

    try {
      const vendor_details = purchaseForm.vendor_details.map((vd) => {
        const cleaned = { ...vd };
        [
          "deliver_date_from_vendor",
          "final_payment_date_to_vendor",
          "vendor",
          "currency_advance",
          "currency_logistic",
          "currency_final_payment",
          "currency_pending_payment",
        ].forEach((f) => {
          if (cleaned[f] === "") cleaned[f] = null;
        });
        return cleaned;
      });

      const payload = {
        sr_no: purchaseForm.sr_no,
        vessel_name: purchaseForm.vessel_name,
        received_po_from_client: purchaseForm.received_po_from_client,
        assigned_team: purchaseForm.assigned_team,
        status: purchaseForm.status,
        vendor_details,
      };

      if (editingPurchase)
        await purchaseAPI.updatePurchase(editingPurchase.id, payload);
      else await purchaseAPI.createPurchase(payload);
      showAlert(
        editingPurchase ? "Updated successfully" : "Created successfully"
      );
      await fetchPurchases();
      closeModal();
    } catch (e) {
      if (e?.response?.data) {
        const data = e.response.data;
        const msg = Object.entries(data)
          .map(([k, v]) => `${k}: ${v.join ? v.join(", ") : v}`)
          .join(" ");
        showAlert(msg, "error");
      } else showAlert("Save error", "error");
    }
  };

  const onDelete = async (id) => {
    if (!perms.canDelete()) return showAlert("No permission", "error");
    if (!window.confirm("Delete this record?")) return;
    try {
      await purchaseAPI.deletePurchase(id);
      showAlert("Deleted");
      await fetchPurchases();
    } catch {
      showAlert("Delete error", "error");
    }
  };

  const exportExcel = async () => {
    try {
      const res = await purchaseAPI.exportExcel();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", "purchase_export.xlsx");
      document.body.appendChild(a);
      a.click();
      a.remove();
      showAlert("Excel exported");
    } catch {
      showAlert("Export failed", "error");
    }
  };

  const purchaseTeamDisabled = editingPurchase
    ? !perms.canEditPurchase() || !perms.canUpdate()
    : !perms.canEditPurchase() || !perms.canCreate();
  const accountsTeamDisabled = editingPurchase
    ? !perms.canEditAccounts() || !perms.canUpdate()
    : !perms.canEditAccounts() || !perms.canCreate();

  return (
    <ThemeProvider theme={getTheme()}>
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg,#f8fafc 0%,#e2e8f0 100%)",
          pb: 4,
        }}
      >
        <Snackbar
          open={alert.open}
          autoHideDuration={4000}
          onClose={() => setAlert((a) => ({ ...a, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            onClose={() => setAlert((a) => ({ ...a, open: false }))}
            severity={alert.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {alert.message}
          </MuiAlert>
        </Snackbar>

        <Container maxWidth="xl" sx={{ py: 2 }}>
          {/* Stats */}
          <Grid container spacing={4} sx={{ mb: 3 }}>
            {[
              {
                key: "total",
                label: "Total Purchases",
                icon: <InventoryRounded />,
                color: "#005b82",
              },
              {
                key: "pending",
                label: "Pending",
                icon: <PendingActionsRounded />,
                color: "#ff9800",
              },
              {
                key: "ordered",
                label: "Ordered",
                icon: <LoopRounded />,
                color: "#2196f3",
              },
              {
                key: "received",
                label: "Received",
                icon: <LocalShippingRounded />,
                color: "#4caf50",
              },
              {
                key: "closed",
                label: "Closed",
                icon: <TaskAltRounded />,
                color: "#4caf50",
              },
              {
                key: "cancelled",
                label: "Cancelled",
                icon: <RefreshRounded />,
                color: "#f44336",
              },
            ].map(({ key, label, icon, color }) => {
              const count =
                key === "total"
                  ? purchases.length
                  : purchases.filter((p) => p.status === key).length;
              const pct = purchases.length
                ? (count / purchases.length) * 100
                : 0;
              return (
                <Grid item xs={12} sm={6} md={2} key={key}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            background: alpha(color, 0.1),
                            color,
                          }}
                        >
                          {icon}
                        </Box>
                        <Typography variant="h4" fontWeight={700} color={color}>
                          {count}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {label}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          mt: 1,
                          backgroundColor: alpha(color, 0.2),
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: color,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {pct.toFixed(1)}% of total
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Controls */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  placeholder="Search purchase records..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRounded />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 280, flex: 1 }}
                />

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    {STATUS_OPTS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<FileDownloadRounded />}
                    onClick={exportExcel}
                  >
                    Export
                  </Button>
                  {perms.canCreate() && (
                    <Button
                      variant="contained"
                      startIcon={<AddRounded />}
                      onClick={() => openModal()}
                      sx={{
                        background:
                          "linear-gradient(135deg,#005b82 0%,#2586bc 100%)",
                      }}
                    >
                      New Purchase
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <LinearProgress sx={{ mb: 2 }} />
                  <Typography color="text.secondary">
                    Loading purchase data...
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>SR</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Vessel</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          PO From Client
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Vendor(s)</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Purchase Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Accounts Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Overall</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filtered.map((p) => {
                        const names = vendorNames(p);
                        return (
                          <TableRow key={p.id} hover>
                            <TableCell>
                              <Typography fontWeight={600}>{p.sr_no}</Typography>
                            </TableCell>
                            <TableCell>{p.vessel_name}</TableCell>
                            <TableCell>
                              {p.received_po_from_client || "-"}
                            </TableCell>
                            <TableCell>
                              {names.length === 0 ? (
                                "-"
                              ) : names.length === 1 ? (
                                names[0]
                              ) : (
                                <Tooltip title={names.join(", ")}>
                                  <Chip
                                    size="small"
                                    label={`${names.length} vendors`}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={statusSummary(
                                  p.vendor_details,
                                  "purchase_status"
                                )}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={statusSummary(
                                  p.vendor_details,
                                  "accounts_status"
                                )}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip label={statusLabel(p.status)} size="small" />
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  justifyContent: "center",
                                }}
                              >
                                <Tooltip
                                  title={
                                    perms.canUpdate() &&
                                    (perms.canEditPurchase() ||
                                      perms.canEditAccounts())
                                      ? "Edit"
                                      : "View"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => openModal(p)}
                                  >
                                    {perms.canUpdate() &&
                                    (perms.canEditPurchase() ||
                                      perms.canEditAccounts()) ? (
                                      <EditRounded fontSize="small" />
                                    ) : (
                                      <VisibilityRounded fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                {perms.canDelete() && (
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => onDelete(p.id)}
                                    >
                                      <DeleteRounded fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {!loading && filtered.length === 0 && (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <InventoryRounded
                    sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    No purchase records found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm || statusFilter !== "all"
                      ? "Try adjusting your search or filter criteria"
                      : "Create your first purchase record to get started"}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Modal */}
          <AnimatePresence>
            {open && (
              <Dialog open onClose={closeModal} maxWidth="lg" fullWidth>
                <DialogTitle
                  sx={{
                    background:
                      "linear-gradient(135deg,#005b82 0%,#2586bc 100%)",
                    color: "white",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PaymentsRounded />
                    <Typography variant="h6">
                      {editingPurchase
                        ? "Edit Purchase Record"
                        : "Create Purchase Record"}
                    </Typography>
                  </Box>
                </DialogTitle>

                <Box sx={{ px: 2, pt: 2 }}>
                  <Stepper activeStep={activeStep} alternativeLabel>
                    {[
                      "Purchase Details",
                      "Vendor Information",
                      "Accounts Information",
                      "Final Details",
                    ].map((label) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>

                <DialogContent>
                  {activeStep === 0 && (
                    <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <TextField
                          name="sr_no"
                          label="SR No *"
                          value={purchaseForm.sr_no}
                          onChange={onChange}
                          fullWidth
                          InputLabelProps={{ sx: { fontWeight: "bold" } }}
                          disabled={purchaseTeamDisabled}
                        />
                        <TextField
                          name="vessel_name"
                          label="Vessel Name *"
                          value={purchaseForm.vessel_name}
                          onChange={onChange}
                          fullWidth
                          InputLabelProps={{ sx: { fontWeight: "bold" } }}
                          disabled={purchaseTeamDisabled}
                        />
                      </Box>
                      <TextField
                        name="received_po_from_client"
                        label="Client PO No"
                        value={purchaseForm.received_po_from_client}
                        onChange={onChange}
                        fullWidth
                        InputLabelProps={{ sx: { fontWeight: "bold" } }}
                        disabled={purchaseTeamDisabled}
                      />
                    </Box>
                  )}

                  {activeStep === 1 && (
                    <Box sx={{ mt: 2 }}>
                      {purchaseForm.vendor_details.map((vd, index) => (
                        <VendorInfoBlock
                          key={index}
                          vd={vd}
                          index={index}
                          vendors={vendors}
                          disabled={purchaseTeamDisabled}
                          onFieldChange={onVendorFieldChange}
                          onVendorSelect={onVendorSelect}
                          onRemove={removeVendorDetail}
                          canRemove={purchaseForm.vendor_details.length > 1}
                        />
                      ))}
                      {!purchaseTeamDisabled && (
                        <Button
                          startIcon={<AddRounded />}
                          onClick={addVendorDetail}
                          variant="outlined"
                        >
                          Add Another Vendor
                        </Button>
                      )}
                    </Box>
                  )}

                  {activeStep === 2 && (
                    <Box sx={{ mt: 2 }}>
                      {purchaseForm.vendor_details.map((vd, index) => (
                        <AccountsInfoBlock
                          key={index}
                          vd={vd}
                          index={index}
                          currencies={currencies}
                          disabled={accountsTeamDisabled}
                          onFieldChange={onVendorFieldChange}
                        />
                      ))}
                    </Box>
                  )}

                  {activeStep === 3 && (
                    <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
                      <TextField
                        name="assigned_team"
                        label="Assigned Team"
                        value={purchaseForm.assigned_team}
                        onChange={onChange}
                        fullWidth
                        InputLabelProps={{ sx: { fontWeight: "bold" } }}
                      />
                      <FormControl fullWidth>
                        <InputLabel sx={{ fontWeight: "bold" }}>
                          Overall Status
                        </InputLabel>
                        <Select
                          name="status"
                          value={purchaseForm.status}
                          label="Overall Status"
                          onChange={onChange}
                        >
                          {STATUS_OPTS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {editingPurchase && (
                        <Card
                          variant="outlined"
                          sx={{ p: 2, background: alpha("#005b82", 0.03) }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            <b>Created by:</b> {editingPurchase.created_by_name}{" "}
                            &nbsp;|&nbsp; <b>Created:</b>{" "}
                            {new Date(
                              editingPurchase.created_at
                            ).toLocaleString()}{" "}
                            &nbsp;|&nbsp; <b>Updated:</b>{" "}
                            {new Date(
                              editingPurchase.updated_at
                            ).toLocaleString()}
                          </Typography>
                        </Card>
                      )}
                    </Box>
                  )}
                </DialogContent>

                <DialogActions>
                  <Button onClick={closeModal}>Cancel</Button>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep((s) => s - 1)}
                    variant="outlined"
                  >
                    Back
                  </Button>
                  {activeStep < 3 ? (
                    <Button
                      variant="contained"
                      onClick={() => setActiveStep((s) => s + 1)}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button variant="contained" onClick={onSubmit}>
                      {editingPurchase ? "Update Record" : "Create Record"}
                    </Button>
                  )}
                </DialogActions>
              </Dialog>
            )}
          </AnimatePresence>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default PurchaseTracker;
